import React, { useState, useEffect, useContext, useMemo } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import CountUp from 'react-countup';
import { DateRangeFilter } from '../components/DateRangeFilter';
import { useTaskDrawer } from '../hooks/useTaskDrawer';
import { TaskDrawer } from '../components/TaskDrawer';
import { usePageHeader } from '../components/Layout';
import { Ic } from '../design/icons';
import { SectionCard, StatusPill, Donut } from '../design/primitives';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export default function Reports() {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [tags, setTags] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);

  const [period, setPeriod] = useState('all');
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [selectedProject, setSelectedProject] = useState('all');
  const [selectedTag, setSelectedTag] = useState('all');
  const [selectedUser, setSelectedUser] = useState('all');

  const { selectedTaskId, isOpen: showDrawer, drawerRef, openDrawer: handleTaskClick, closeDrawer: handleCloseDrawer } = useTaskDrawer();

  const canSelectUser = user && ['admin', 'proje_yoneticisi', 'takim_lideri'].includes(user.role);

  // ============== PDF GENERATION (Preserved) ==============
  const generatePDF = async () => {
    if (!reportData) return;
    const { jsPDF } = await import('jspdf');
    const autoTableModule = await import('jspdf-autotable');
    const autoTable = autoTableModule.default;
    const { poppinsMedium, poppinsBold } = await import('../utils/poppinsFont');
    const doc = new jsPDF('p', 'mm', 'a4');

    doc.addFileToVFS('Poppins-Medium.ttf', poppinsMedium);
    doc.addFileToVFS('Poppins-Bold.ttf', poppinsBold);
    doc.addFont('Poppins-Medium.ttf', 'Poppins', 'normal');
    doc.addFont('Poppins-Bold.ttf', 'Poppins', 'bold');
    doc.setFont('Poppins');

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 14;
    let y = 0;

    const hexToRgb = (hex) => {
      const h = (hex || '#1E5FA8').replace('#', '');
      return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
    };
    const tintColor = (rgb, amount) => rgb.map(c => Math.round(255 - amount * (255 - c)));

    const tagColorMap = {};
    (reportData.tagSummaries || []).forEach(ts => {
      tagColorMap[ts.tagId] = { name: ts.tagName, color: ts.tagColor || '#1E5FA8' };
    });
    tags.forEach(t => {
      if (!tagColorMap[t.id]) tagColorMap[t.id] = { name: t.name, color: t.color || '#1E5FA8' };
    });

    const periodLabels = { 'all': 'Tüm Zamanlar', 'this_month': 'Bu Ay', 'prev_month': 'Önceki Ay', 'this_year': 'Bu Yıl', 'prev_year': 'Önceki Yıl', 'custom': 'Özel Aralık' };
    const selectedProjectName = selectedProject === 'all' ? 'Tüm Projeler' : (projects.find(p => p.id === selectedProject)?.name || '');
    const selectedUserName = selectedUser === 'all' ? 'Tüm Kullanıcılar' : (availableUsers.find(u => u.id === selectedUser)?.name || '');
    const selectedTagName = selectedTag === 'all' ? 'Tüm Etiketler' : (tags.find(t => t.id === selectedTag)?.name || '');

    // Header
    doc.setFillColor(30, 95, 168);
    doc.rect(0, 0, pageWidth, 36, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('Poppins', 'bold');
    doc.text('ONXFLOW', margin, 15);
    doc.setFontSize(10);
    doc.setFont('Poppins', 'normal');
    doc.setTextColor(200, 220, 240);
    doc.text('Detaylı Rapor', margin, 23);

    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    doc.setFont('Poppins', 'bold');
    const periodText = period === 'custom' && customStartDate && customEndDate
      ? `${customStartDate.toLocaleDateString('tr-TR')} - ${customEndDate.toLocaleDateString('tr-TR')}`
      : (periodLabels[period] || period);
    doc.text(periodText, pageWidth - margin, 18, { align: 'right' });
    y = 42;

    // Filter row
    doc.setFillColor(241, 245, 249);
    doc.roundedRect(margin, y, pageWidth - margin * 2, 9, 1.5, 1.5, 'F');
    doc.setTextColor(71, 85, 105);
    doc.setFontSize(7.5);
    doc.setFont('Poppins', 'normal');
    doc.text(`Proje: ${selectedProjectName}   |   Kullanıcı: ${selectedUserName}   |   Etiket: ${selectedTagName}`, margin + 4, y + 6);
    y += 14;

    // Stats
    const statBoxWidth = (pageWidth - margin * 2 - 8) / 3;
    const stats = [
      { label: 'Toplam Görev', value: String(reportData.totalTasks || 0), accent: [30, 95, 168] },
      { label: 'Toplam Süre', value: reportData.totalDuration || '00:00:00', accent: [108, 76, 169] },
      { label: 'Toplam Adet', value: String(reportData.totalQuantity || 0), accent: [163, 71, 10] }
    ];
    stats.forEach((stat, i) => {
      const sx = margin + i * (statBoxWidth + 4);
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.4);
      doc.roundedRect(sx, y, statBoxWidth, 18, 2, 2, 'FD');
      doc.setFillColor(...stat.accent);
      doc.roundedRect(sx, y, 1.5, 18, 1, 1, 'F');
      doc.setFontSize(7);
      doc.setFont('Poppins', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(stat.label, sx + 6, y + 6.5);
      doc.setFontSize(13);
      doc.setFont('Poppins', 'bold');
      doc.setTextColor(...stat.accent);
      doc.text(stat.value, sx + 6, y + 14.5);
    });
    y += 24;

    // Category cards
    const visibleTags = (reportData.tagSummaries || []).filter(t => t.taskCount > 0 && (t.totalQuantity > 0 || t.totalDurationSeconds > 0));
    if (visibleTags.length > 0) {
      doc.setFillColor(30, 95, 168);
      doc.roundedRect(margin, y, 3, 6, 1, 1, 'F');
      doc.setTextColor(17, 24, 39);
      doc.setFontSize(12);
      doc.setFont('Poppins', 'bold');
      doc.text('Etiket Bazlı Detaylar', margin + 6, y + 5);
      y += 10;

      const cardWidth = (pageWidth - margin * 2 - 8) / 3;
      const cardHeight = 28;

      visibleTags.forEach((tag, idx) => {
        const col = idx % 3;
        const row = Math.floor(idx / 3);
        const cx = margin + col * (cardWidth + 4);
        const cy = y + row * (cardHeight + 3);
        if (cy + cardHeight > pageHeight - 20) { doc.addPage(); y = margin; return; }

        const [r, g, b] = hexToRgb(tag.tagColor);
        doc.setFillColor(...tintColor([r, g, b], 0.08));
        doc.setDrawColor(r, g, b);
        doc.setLineWidth(0.3);
        doc.roundedRect(cx, cy, cardWidth, cardHeight, 2, 2, 'FD');
        doc.setFillColor(r, g, b);
        doc.roundedRect(cx, cy, 2, cardHeight, 1, 1, 'F');

        doc.setTextColor(17, 24, 39);
        doc.setFontSize(9);
        doc.setFont('Poppins', 'bold');
        doc.text(tag.tagName || '', cx + 6, cy + 7);

        doc.setFillColor(r, g, b);
        const countText = String(tag.taskCount);
        const countWidth = doc.getTextWidth(countText) + 5;
        doc.roundedRect(cx + cardWidth - countWidth - 4, cy + 3, countWidth, 5.5, 1.5, 1.5, 'F');
        doc.setFontSize(7);
        doc.setFont('Poppins', 'bold');
        doc.setTextColor(255, 255, 255);
        doc.text(countText, cx + cardWidth - countWidth / 2 - 4 + 0.5, cy + 7, { align: 'center' });

        let bx = cx + 6;
        const by = cy + 15;
        if (tag.totalDuration && tag.totalDuration !== '00:00:00') {
          doc.setFontSize(7);
          doc.setFont('Poppins', 'normal');
          doc.setTextColor(100, 116, 139);
          doc.text('Süre:', bx, by);
          doc.setFont('Poppins', 'bold');
          doc.setTextColor(108, 76, 169);
          doc.text(tag.totalDuration, bx + 12, by);
          bx += 35;
        }
        if (tag.totalQuantity > 0) {
          doc.setFontSize(7);
          doc.setFont('Poppins', 'normal');
          doc.setTextColor(100, 116, 139);
          doc.text('Adet:', bx, by);
          doc.setFont('Poppins', 'bold');
          doc.setTextColor(163, 71, 10);
          doc.text(String(tag.totalQuantity), bx + 12, by);
        }
      });

      const totalRows = Math.ceil(visibleTags.length / 3);
      y += totalRows * (cardHeight + 3) + 6;
    }

    if (y > pageHeight - 40) { doc.addPage(); y = margin; }

    // Tasks table
    const taskRows = (reportData.tasks || []).filter(task => {
      const completedSteps = task.assignmentHistory?.filter(a => {
        if (!a.completedAt) return false;
        if (selectedUser !== 'all') return a.userId === selectedUser;
        return true;
      }) || [];
      return completedSteps.length > 0;
    });

    if (taskRows.length > 0) {
      doc.setFillColor(30, 95, 168);
      doc.roundedRect(margin, y, 3, 6, 1, 1, 'F');
      doc.setTextColor(17, 24, 39);
      doc.setFontSize(12);
      doc.setFont('Poppins', 'bold');
      doc.text(`Görevler (${taskRows.length})`, margin + 6, y + 5);
      y += 10;

      const statusLabels = { 'beklemede': 'Beklemede', 'işlemde': 'İşlemde', 'incelemede': 'İncelemede', 'tamamlandı': 'Tamamlandı' };
      const statusColors = {
        'beklemede': [138, 144, 154],
        'işlemde': [30, 95, 168],
        'incelemede': [108, 76, 169],
        'tamamlandı': [26, 127, 79]
      };

      taskRows.sort((a, b) => {
        const getLastDate = (task) => (task.assignmentHistory || []).reduce((latest, entry) => {
          const dates = [entry.completedAt, entry.assignedAt, entry.reassignedAt].filter(Boolean);
          for (const d of dates) { if (!latest || d > latest) latest = d; }
          return latest;
        }, null);
        return (getLastDate(b) || '').localeCompare(getLastDate(a) || '');
      });

      const tableData = taskRows.map(task => {
        const project = projects.find(p => p.id === task.projectId);
        const completedSteps = task.assignmentHistory?.filter(a => {
          if (!a.completedAt) return false;
          if (selectedUser !== 'all') return a.userId === selectedUser;
          return true;
        }) || [];
        const lastActionDate = (task.assignmentHistory || []).reduce((latest, a) => {
          const dates = [a.completedAt, a.assignedAt, a.reassignedAt].filter(Boolean);
          for (const d of dates) { if (!latest || d > latest) latest = d; }
          return latest;
        }, null);
        return [
          task.title || '',
          project?.name || '-',
          statusLabels[task.status] || task.status || '',
          `${completedSteps.length}`,
          lastActionDate ? new Date(lastActionDate).toLocaleDateString('tr-TR') : '-'
        ];
      });
      const rowStatusKeys = taskRows.map(task => task.status);

      autoTable(doc, {
        startY: y,
        head: [['Görev', 'Proje', 'Durum', 'Adım', 'Tarih']],
        body: tableData,
        margin: { left: margin, right: margin },
        styles: { fontSize: 7.5, cellPadding: { top: 3, right: 2, bottom: 3, left: 2 }, overflow: 'linebreak', font: 'Poppins', textColor: [51, 65, 85], lineColor: [226, 232, 240], lineWidth: 0.2 },
        headStyles: { fillColor: [17, 24, 39], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 7.5, cellPadding: { top: 3.5, right: 2, bottom: 3.5, left: 2 } },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        columnStyles: { 0: { cellWidth: 80 }, 1: { cellWidth: 44 }, 2: { cellWidth: 22, halign: 'center' }, 3: { cellWidth: 14, halign: 'center' }, 4: { cellWidth: 22, halign: 'center' } },
        willDrawCell: (data) => {
          if (data.section === 'body' && data.column.index === 2) {
            const sc = statusColors[rowStatusKeys[data.row.index]];
            if (sc) doc.setFillColor(...tintColor(sc, 0.12));
          }
        },
        didDrawCell: (data) => {
          if (data.section === 'body' && data.column.index === 2) {
            const sc = statusColors[rowStatusKeys[data.row.index]];
            if (sc) { doc.setFillColor(sc[0], sc[1], sc[2]); doc.rect(data.cell.x, data.cell.y, 1, data.cell.height, 'F'); }
          }
        },
      });
    }

    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.3);
      doc.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);
      doc.setFontSize(6.5);
      doc.setFont('Poppins', 'normal');
      doc.setTextColor(148, 163, 184);
      doc.text('ONXFLOW', margin, pageHeight - 7);
      doc.text(new Date().toLocaleDateString('tr-TR'), pageWidth / 2, pageHeight - 7, { align: 'center' });
      doc.setFont('Poppins', 'bold');
      doc.text(`${i} / ${totalPages}`, pageWidth - margin, pageHeight - 7, { align: 'right' });
    }

    const pdfBlob = doc.output('blob');
    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, '_blank');
  };

  // ============== PERFORMANCE PDF (Preserved) ==============
  const generatePerformancePDF = async () => {
    if (!reportData || !reportData.tasks) return;
    const { jsPDF } = await import('jspdf');
    const autoTableModule = await import('jspdf-autotable');
    const autoTable = autoTableModule.default;
    const { poppinsMedium, poppinsBold } = await import('../utils/poppinsFont');

    const doc = new jsPDF('p', 'mm', 'a4');
    doc.addFileToVFS('Poppins-Medium.ttf', poppinsMedium);
    doc.addFileToVFS('Poppins-Bold.ttf', poppinsBold);
    doc.addFont('Poppins-Medium.ttf', 'Poppins', 'normal');
    doc.addFont('Poppins-Bold.ttf', 'Poppins', 'bold');
    doc.setFont('Poppins');

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 14;

    const hexToRgb = (hex) => {
      const h = (hex || '#1E5FA8').replace('#', '');
      return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
    };
    const tintColor = (rgb, amount) => rgb.map(c => Math.round(255 - amount * (255 - c)));

    const tagMap = {};
    (reportData.tagSummaries || []).forEach(ts => { tagMap[ts.tagId] = { name: ts.tagName, color: ts.tagColor || '#1E5FA8' }; });
    tags.forEach(t => { if (!tagMap[t.id]) tagMap[t.id] = { name: t.name, color: t.color || '#1E5FA8' }; });

    const userMap = {};
    (availableUsers || []).forEach(u => { userMap[u.id] = u.name; });

    const parseDuration = (val) => {
      if (!val) return 0;
      const str = String(val);
      const parts = str.split(':');
      if (parts.length === 3) return parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseInt(parts[2]);
      if (parts.length === 2) return parseInt(parts[0]) * 60 + parseInt(parts[1]);
      const num = parseFloat(str);
      return isNaN(num) ? 0 : num;
    };

    const activeTagIds = new Set((reportData.tagSummaries || []).map(ts => ts.tagId));
    const perfData = {};

    (reportData.tasks || []).forEach(task => {
      const oldestPerUserTag = {};
      (task.assignmentHistory || []).forEach(a => {
        if (!a.completedAt || !a.userId || !a.completedTagId) return;
        if (period === 'custom' && customStartDate && customEndDate) {
          const completedDate = new Date(a.completedAt);
          if (completedDate < customStartDate || completedDate > new Date(customEndDate.getTime() + 86400000)) return;
        }
        if (selectedUser !== 'all' && a.userId !== selectedUser) return;
        if (selectedTag !== 'all' && a.completedTagId !== selectedTag) return;
        if (!activeTagIds.has(a.completedTagId)) return;
        const oKey = `${a.userId}::${a.completedTagId}`;
        if (!oldestPerUserTag[oKey] || a.completedAt < oldestPerUserTag[oKey].completedAt) oldestPerUserTag[oKey] = a;
      });

      Object.values(oldestPerUserTag).forEach(a => {
        const tagId = a.completedTagId;
        const tag = tagMap[tagId] || { name: 'Etiketsiz', color: '#9ca3af' };
        const qType = a.completedQuantityType || a.quantityType || task.quantityType || 'adet';
        const key = `${a.userId}::${tagId}`;
        if (!perfData[key]) {
          perfData[key] = { userId: a.userId, userName: userMap[a.userId] || a.userId, tagId, tagName: tag.name, tagColor: tag.color, qType, totalValue: 0, taskCount: 0 };
        }
        perfData[key].taskCount += 1;
        const rawVal = a.completedQuantityValue || a.quantityValue || task.quantityValue;
        const isSure = qType === 'süre' || qType === 'sure';
        if (isSure) perfData[key].totalValue += parseDuration(rawVal);
        else perfData[key].totalValue += parseFloat(rawVal || 0) || 0;
      });
    });

    const rows = Object.values(perfData).sort((a, b) => {
      const tagCompare = a.tagName.localeCompare(b.tagName, 'tr');
      return tagCompare !== 0 ? tagCompare : a.userName.localeCompare(b.userName, 'tr');
    });

    if (rows.length === 0) {
      toast.error('Seçilen filtrelere uygun kullanıcı performans verisi bulunamadı.');
      return;
    }

    const periodLabels = { 'all': 'Tüm Zamanlar', 'this_month': 'Bu Ay', 'prev_month': 'Önceki Ay', 'this_year': 'Bu Yıl', 'prev_year': 'Önceki Yıl', 'custom': 'Özel Aralık' };
    const periodText = period === 'custom' && customStartDate && customEndDate
      ? `${customStartDate.toLocaleDateString('tr-TR')} - ${customEndDate.toLocaleDateString('tr-TR')}`
      : (periodLabels[period] || period);
    const selectedProjectName = selectedProject === 'all' ? 'Tüm Projeler' : (projects.find(p => p.id === selectedProject)?.name || '');
    const selectedTagName = selectedTag === 'all' ? 'Tüm Etiketler' : (tags.find(t => t.id === selectedTag)?.name || '');

    const formatDur = (s) => {
      if (!s || s <= 0) return '-';
      const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = Math.floor(s % 60);
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
    };

    let y = 0;
    doc.setFillColor(30, 95, 168);
    doc.rect(0, 0, pageWidth, 36, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.setFont('Poppins', 'bold');
    doc.text('ONXFLOW', margin, 15);
    doc.setFontSize(10);
    doc.setFont('Poppins', 'normal');
    doc.setTextColor(200, 220, 240);
    doc.text('Kullanıcı Performans Raporu', margin, 23);
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    doc.setFont('Poppins', 'bold');
    doc.text(periodText, pageWidth - margin, 18, { align: 'right' });
    y = 42;

    doc.setFillColor(241, 245, 249);
    doc.roundedRect(margin, y, pageWidth - margin * 2, 9, 1.5, 1.5, 'F');
    doc.setTextColor(71, 85, 105);
    doc.setFontSize(7.5);
    doc.setFont('Poppins', 'normal');
    doc.text(`Proje: ${selectedProjectName}   |   Etiket: ${selectedTagName}   |   Dönem: ${periodText}`, margin + 4, y + 6);
    y += 14;

    // Summary
    const uniqueUsers = [...new Set(rows.map(r => r.userId))].length;
    const uniqueCategories = [...new Set(rows.map(r => r.tagId))].length;
    const statBoxWidth = (pageWidth - margin * 2 - 8) / 3;
    const summaryStats = [
      { label: 'Kullanıcı Sayısı', value: String(uniqueUsers), accent: [30, 95, 168] },
      { label: 'Etiket Sayısı', value: String(uniqueCategories), accent: [108, 76, 169] },
      { label: 'Toplam Görev Sayısı', value: String(reportData.totalTasks || 0), accent: [26, 127, 79] }
    ];
    summaryStats.forEach((stat, i) => {
      const sx = margin + i * (statBoxWidth + 4);
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.4);
      doc.roundedRect(sx, y, statBoxWidth, 18, 2, 2, 'FD');
      doc.setFillColor(...stat.accent);
      doc.roundedRect(sx, y, 1.5, 18, 1, 1, 'F');
      doc.setFontSize(7);
      doc.setFont('Poppins', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(stat.label, sx + 6, y + 6.5);
      doc.setFontSize(13);
      doc.setFont('Poppins', 'bold');
      doc.setTextColor(...stat.accent);
      doc.text(stat.value, sx + 6, y + 14.5);
    });
    y += 24;

    // Grouped table
    const groupedByTag = {};
    rows.forEach(r => {
      if (!groupedByTag[r.tagId]) groupedByTag[r.tagId] = { tagName: r.tagName, tagColor: r.tagColor, users: [] };
      groupedByTag[r.tagId].users.push(r);
    });

    const tableBody = [];
    const rowMeta = [];
    const rowColors = [];
    Object.values(groupedByTag).forEach(group => {
      const color = hexToRgb(group.tagColor);
      tableBody.push([group.tagName, '', '', '', '']);
      rowMeta.push('header'); rowColors.push(color);
      let subtotalTasks = 0, subtotalValue = 0, isSure = false;
      group.users.forEach((r, i) => {
        isSure = r.qType === 'süre' || r.qType === 'sure';
        const valText = isSure ? formatDur(r.totalValue) : String(r.totalValue);
        tableBody.push(['', r.userName, String(r.taskCount), valText, isSure ? 'Süre' : (r.qType || 'Adet')]);
        rowMeta.push(i === 0 ? 'data_first' : 'data');
        rowColors.push(color);
        subtotalTasks += r.taskCount;
        subtotalValue += r.totalValue;
      });
      const subText = isSure ? formatDur(subtotalValue) : String(subtotalValue);
      tableBody.push(['Toplam', '', String(subtotalTasks), subText, '']);
      rowMeta.push('subtotal'); rowColors.push(color);
    });

    autoTable(doc, {
      startY: y,
      head: [['Etiket', 'Kullanıcı', 'Görev', 'Değer', 'Tip']],
      body: tableBody,
      margin: { left: margin, right: margin },
      styles: { fontSize: 7.5, cellPadding: 2.5, font: 'Poppins', textColor: [51, 65, 85], lineColor: [226, 232, 240], lineWidth: 0.2 },
      headStyles: { fillColor: [17, 24, 39], textColor: [255, 255, 255], fontStyle: 'bold' },
      columnStyles: { 0: { cellWidth: 50 }, 1: { cellWidth: 50 }, 2: { cellWidth: 20, halign: 'center' }, 3: { cellWidth: 30, halign: 'center' }, 4: { cellWidth: 24, halign: 'center' } },
      willDrawCell: (data) => {
        if (data.section === 'body') {
          const meta = rowMeta[data.row.index];
          const color = rowColors[data.row.index];
          if (meta === 'header') { doc.setFillColor(...tintColor(color, 0.15)); doc.setTextColor(color[0], color[1], color[2]); }
          else if (meta === 'data_first') { doc.setFillColor(...tintColor(color, 0.06)); }
          else if (meta === 'subtotal') { doc.setFillColor(...tintColor(color, 0.08)); doc.setTextColor(color[0], color[1], color[2]); }
          else if (meta === 'data') { doc.setFillColor(data.row.index % 2 === 0 ? 255 : 248, data.row.index % 2 === 0 ? 255 : 250, data.row.index % 2 === 0 ? 255 : 252); }
        }
      },
      didDrawCell: (data) => {
        if (data.section === 'body') {
          const meta = rowMeta[data.row.index];
          const color = rowColors[data.row.index];
          if (meta === 'header' && data.column.index === 0) {
            doc.setFillColor(color[0], color[1], color[2]);
            doc.rect(data.cell.x, data.cell.y, 2, data.cell.height, 'F');
          }
        }
      }
    });

    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.3);
      doc.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);
      doc.setFontSize(6.5);
      doc.setFont('Poppins', 'normal');
      doc.setTextColor(148, 163, 184);
      doc.text('ONXFLOW', margin, pageHeight - 7);
      doc.text(new Date().toLocaleDateString('tr-TR'), pageWidth / 2, pageHeight - 7, { align: 'center' });
      doc.setFont('Poppins', 'bold');
      doc.text(`${i} / ${totalPages}`, pageWidth - margin, pageHeight - 7, { align: 'right' });
    }

    const pdfBlob = doc.output('blob');
    window.open(URL.createObjectURL(pdfBlob), '_blank');
  };

  // ============== DATA LOADING ==============
  useEffect(() => {
    const loadInitial = async () => {
      await Promise.all([fetchProjects(), fetchTags(), canSelectUser && fetchAvailableUsers()].filter(Boolean));
      fetchReport();
    };
    loadInitial();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (period === 'custom' && (!customStartDate || !customEndDate)) return;
    fetchReport();
    // eslint-disable-next-line
  }, [period, selectedProject, selectedTag, selectedUser, customStartDate, customEndDate]);

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/projects`);
      setProjects(res.data.sort((a, b) => a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' })));
    } catch {}
  };

  const fetchTags = async () => {
    try {
      const res = await axios.get(`${API}/tags`);
      setTags(res.data.sort((a, b) => a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' })));
    } catch {}
  };

  const fetchAvailableUsers = async () => {
    if (!canSelectUser) return;
    try {
      const res = await axios.get(`${API}/reports/available-users`);
      setAvailableUsers(res.data.sort((a, b) => a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' })));
    } catch {}
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = {
        period,
        ...(selectedProject !== 'all' && { project_id: selectedProject }),
        ...(selectedTag !== 'all' && { tag_id: selectedTag }),
        ...(selectedUser !== 'all' && { user_id: selectedUser }),
        ...(period === 'custom' && customStartDate && { start_date: customStartDate.toISOString() }),
        ...(period === 'custom' && customEndDate && { end_date: new Date(customEndDate.getTime() + 86400000 - 1).toISOString() })
      };
      const res = await axios.get(`${API}/reports/user-summary`, { params });
      setReportData(res.data);
    } catch (error) {
      toast.error('Rapor yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const visibleTagSummaries = useMemo(() => {
    if (!reportData?.tagSummaries) return [];
    return reportData.tagSummaries.filter(t =>
      t.taskCount > 0 && (t.totalQuantity > 0 || t.totalDurationSeconds > 0)
    );
  }, [reportData]);

  const taskRows = useMemo(() => {
    if (!reportData?.tasks) return [];
    return reportData.tasks.map(task => {
      const completedSteps = task.assignmentHistory?.filter(a => {
        if (!a.completedAt) return false;
        if (selectedUser !== 'all') return a.userId === selectedUser;
        return true;
      }) || [];
      if (completedSteps.length === 0) return null;
      const lastActionDate = (task.assignmentHistory || []).reduce((latest, a) => {
        const dates = [a.completedAt, a.assignedAt, a.reassignedAt].filter(Boolean);
        for (const d of dates) { if (!latest || d > latest) latest = d; }
        return latest;
      }, null);
      const project = projects.find(p => p.id === task.projectId);
      return { task, completedSteps: completedSteps.length, lastActionDate, projectName: project?.name || '-' };
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (!a.lastActionDate && !b.lastActionDate) return 0;
      if (!a.lastActionDate) return 1;
      if (!b.lastActionDate) return -1;
      return b.lastActionDate.localeCompare(a.lastActionDate);
    });
  }, [reportData, projects, selectedUser]);

  const actions = reportData && reportData.tagSummaries && reportData.tagSummaries.length > 0 ? (
    <div className="flex items-center gap-2">
      <button onClick={generatePDF} className="ox-btn" title="PDF Rapor">
        {Ic.download} PDF İndir
      </button>
      {(user?.role === 'admin' || user?.role === 'super_admin') && (
        <button onClick={generatePerformancePDF} className="ox-btn ox-btn-primary" title="Kullanıcı Performans Raporu">
          {Ic.trend} Performans PDF
        </button>
      )}
    </div>
  ) : null;

  usePageHeader([{ label: 'Raporlar' }], actions, [reportData, user]);

  const donutSegments = useMemo(() =>
    visibleTagSummaries.slice(0, 8).map(t => ({
      label: t.tagName,
      value: t.taskCount,
      color: t.tagColor || '#1E5FA8',
    })),
    [visibleTagSummaries]
  );

  return (
    <div className="ox-page space-y-4">
      {/* Filters */}
      <div className="ox-surface rounded-md p-4">
        <div className={canSelectUser ? 'ox-grid-form-4' : 'ox-grid-form-3'}>
          <div className="ox-field">
            <label>Dönem</label>
            <DateRangeFilter
              period={period}
              onPeriodChange={setPeriod}
              startDate={customStartDate}
              endDate={customEndDate}
              onStartDateChange={setCustomStartDate}
              onEndDateChange={setCustomEndDate}
              showLabel={false}
            />
          </div>
          <div className="ox-field">
            <label>Proje</label>
            <select className="ox-select" value={selectedProject} onChange={e => setSelectedProject(e.target.value)}>
              <option value="all">Tüm Projeler</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          {canSelectUser && (
            <div className="ox-field">
              <label>Kullanıcı</label>
              <select className="ox-select" value={selectedUser} onChange={e => setSelectedUser(e.target.value)}>
                <option value="all">Tüm Kullanıcılar</option>
                {availableUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
          )}
          <div className="ox-field">
            <label>Etiket</label>
            <select className="ox-select" value={selectedTag} onChange={e => setSelectedTag(e.target.value)}>
              <option value="all">Tüm Etiketler</option>
              {tags.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="ox-surface rounded-md p-12 text-center ox-text-muted">Rapor yükleniyor...</div>
      ) : !reportData || reportData.totalTasks === 0 ? (
        <div className="ox-surface rounded-md p-12 text-center">
          <div className="ox-text-dim inline-flex mb-3">{Ic.reports}</div>
          <div className="text-[15px] font-semibold mb-1">Henüz tamamlanmış görev yok</div>
          <div className="text-[12.5px] ox-text-muted">Seçilen filtrelere uygun tamamlanmış görev bulunmamaktadır.</div>
        </div>
      ) : (
        <>
          {/* KPI Row */}
          <div className="ox-grid-kpi-3">
            <div className="ox-surface rounded-md p-4">
              <div className="text-[11.5px] ox-text-muted uppercase tracking-wider">Toplam Görev</div>
              <div className="ox-tnum text-[32px] font-semibold leading-none mt-2" style={{ color: 'var(--accent)' }}>
                <CountUp end={reportData.totalTasks || 0} duration={1.2} />
              </div>
            </div>
            <div className="ox-surface rounded-md p-4">
              <div className="text-[11.5px] ox-text-muted uppercase tracking-wider">Toplam Süre</div>
              <div className="ox-tnum text-[32px] font-semibold leading-none mt-2" style={{ color: 'var(--review)' }}>
                {reportData.totalDuration || '00:00:00'}
              </div>
            </div>
            <div className="ox-surface rounded-md p-4">
              <div className="text-[11.5px] ox-text-muted uppercase tracking-wider">Toplam Adet</div>
              <div className="ox-tnum text-[32px] font-semibold leading-none mt-2" style={{ color: 'var(--good)' }}>
                <CountUp end={reportData.totalQuantity || 0} duration={1.2} />
              </div>
            </div>
          </div>

          {/* Tag Summary + Distribution */}
          {visibleTagSummaries.length > 0 && (
            <div className="ox-grid-main-2-1">
              <SectionCard title="Etiket Bazlı Detaylar" subtitle={`${visibleTagSummaries.length} etiket`}>
                <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))' }}>
                  {visibleTagSummaries.map(tag => (
                    <div key={tag.tagId} className="p-3 rounded" style={{
                      background: 'var(--surface-muted)',
                      borderLeft: `3px solid ${tag.tagColor || '#1E5FA8'}`,
                    }}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span style={{ width: 8, height: 8, borderRadius: 2, background: tag.tagColor || '#1E5FA8', flexShrink: 0 }} />
                          <div className="text-[13px] font-semibold truncate">{tag.tagName}</div>
                        </div>
                        <div className="ox-mono text-[11px] px-1.5 py-0.5 rounded" style={{ background: tag.tagColor || '#1E5FA8', color: '#fff' }}>
                          {tag.taskCount}
                        </div>
                      </div>
                      <div className="flex items-center gap-4 text-[11.5px] ox-text-muted">
                        {tag.totalDuration && tag.totalDuration !== '00:00:00' && (
                          <div className="flex items-center gap-1">
                            {Ic.clock}
                            <span className="ox-mono font-medium" style={{ color: 'var(--review)' }}>{tag.totalDuration}</span>
                          </div>
                        )}
                        {tag.totalQuantity > 0 && (
                          <div className="flex items-center gap-1">
                            <span style={{ fontSize: 14 }}>#</span>
                            <span className="ox-mono font-medium" style={{ color: 'var(--good)' }}>
                              <CountUp end={tag.totalQuantity} duration={1.2} decimals={tag.totalQuantity % 1 !== 0 ? 1 : 0} />
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </SectionCard>

              <SectionCard title="Dağılım" subtitle="Görev sayısı">
                <div className="flex items-center justify-center">
                  <Donut
                    segments={donutSegments}
                    size={160}
                    thickness={18}
                    centerValue={reportData.totalTasks || 0}
                    centerLabel="görev"
                  />
                </div>
                <div className="mt-3 space-y-1.5 text-[11.5px]">
                  {donutSegments.slice(0, 5).map((s, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: s.color }} />
                      <span className="ox-text-muted truncate flex-1">{s.label}</span>
                      <span className="ox-tnum font-medium">{s.value}</span>
                    </div>
                  ))}
                </div>
              </SectionCard>
            </div>
          )}

          {/* Tasks Table */}
          {taskRows.length > 0 && (
            <SectionCard title="Görevler" subtitle={`${taskRows.length} görev`} pad={false}>
              <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      <th className="text-left px-4 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium">Görev</th>
                      <th className="text-left px-4 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium hidden md:table-cell">Proje</th>
                      <th className="text-left px-4 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium">Durum</th>
                      <th className="text-left px-4 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium">Adım</th>
                      <th className="text-left px-4 py-2.5 text-[11px] ox-text-muted uppercase tracking-wider font-medium hidden md:table-cell">Tarih</th>
                    </tr>
                  </thead>
                  <tbody>
                    {taskRows.map(({ task, completedSteps, lastActionDate, projectName }) => (
                      <tr
                        key={task.id}
                        onClick={() => handleTaskClick(task.id)}
                        className="cursor-pointer"
                        style={{ borderBottom: '1px solid var(--border)' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-muted)'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}
                      >
                        <td className="px-4 py-2.5">
                          <div className="font-medium truncate" style={{ maxWidth: 360 }}>{task.title}</div>
                        </td>
                        <td className="px-4 py-2.5 ox-text-muted hidden md:table-cell truncate" style={{ maxWidth: 200 }}>{projectName}</td>
                        <td className="px-4 py-2.5"><StatusPill s={task.status} /></td>
                        <td className="px-4 py-2.5 ox-tnum ox-text-muted">{completedSteps}</td>
                        <td className="px-4 py-2.5 ox-mono ox-text-muted hidden md:table-cell">
                          {lastActionDate ? new Date(lastActionDate).toLocaleDateString('tr-TR') : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>
          )}
        </>
      )}

      <TaskDrawer
        selectedTaskId={selectedTaskId}
        isOpen={showDrawer}
        drawerRef={drawerRef}
        onClose={handleCloseDrawer}
        onDataChanged={() => {
          const params = {
            period, nocache: Date.now(),
            ...(selectedProject !== 'all' && { project_id: selectedProject }),
            ...(selectedTag !== 'all' && { tag_id: selectedTag }),
            ...(selectedUser !== 'all' && { user_id: selectedUser }),
            ...(period === 'custom' && customStartDate && { start_date: customStartDate.toISOString() }),
            ...(period === 'custom' && customEndDate && { end_date: new Date(customEndDate.getTime() + 86400000 - 1).toISOString() })
          };
          axios.get(`${API}/reports/user-summary`, { params }).then(res => setReportData(res.data)).catch(() => {});
        }}
      />
    </div>
  );
}
